import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import httpRequest from '../../ajax';
import { queryKeys } from '../../api/queryKeys';
import { buildJobsUrl } from '../../api/buildJobsUrl';
import {
  GET_COUNTRIES,
  GET_STATES,
  GET_INDUSTRIES,
  GET_JOB_LEVELS,
  GET_ALL_SKILLS,
  GET_ALL_JOBS,
  GET_MY_JOBS,
  GET_NOTIFICATIONS,
  GET_LEAGUE,
  GET_MY_RECRUITER_DATA,
  GET_ALL_EXPERT,
  GET_ALL_EXPERT_CATEGORY,
  GET_PROFILE,
  GET_JOB_SEEKER_PLANS,
  GET_MY_JOB_SEEKER_SUBSCRIPTION,
} from '../../redux/actionTypes';

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

export function useCountries(enabled = true) {
  const dispatch = useDispatch();
  const query = useQuery({
    queryKey: queryKeys.countries,
    queryFn: async () => {
      const { data: { data } } = await httpRequest('get', '/country');
      return data;
    },
    enabled,
    staleTime: DAY,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
  useEffect(() => {
    if (query.data) dispatch({ type: GET_COUNTRIES, payload: query.data });
  }, [query.data, dispatch]);
  return query;
}

export function useCountryStates(countryId, enabled = true) {
  const dispatch = useDispatch();
  const query = useQuery({
    queryKey: queryKeys.countryStates(countryId),
    queryFn: async () => {
      const { data: { data } } = await httpRequest('get', `/country/${countryId}/states`);
      return data;
    },
    enabled: Boolean(countryId) && enabled,
    staleTime: DAY,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
  useEffect(() => {
    if (query.data) dispatch({ type: GET_STATES, payload: query.data });
  }, [query.data, dispatch]);
  return query;
}

export function useIndustries(enabled = true) {
  const dispatch = useDispatch();
  const query = useQuery({
    queryKey: queryKeys.industries,
    queryFn: async () => {
      const { data: { data } } = await httpRequest('get', '/industry');
      return data;
    },
    enabled,
    staleTime: HOUR,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
  useEffect(() => {
    if (query.data) dispatch({ type: GET_INDUSTRIES, payload: query.data });
  }, [query.data, dispatch]);
  return query;
}

export function useJobLevels(enabled = true) {
  const dispatch = useDispatch();
  const query = useQuery({
    queryKey: queryKeys.jobLevels,
    queryFn: async () => {
      const { data: { data } } = await httpRequest('get', '/jobs/levels');
      return data;
    },
    enabled,
    staleTime: HOUR,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
  useEffect(() => {
    if (query.data) dispatch({ type: GET_JOB_LEVELS, payload: query.data });
  }, [query.data, dispatch]);
  return query;
}

export function useAllSkills(enabled = true) {
  const dispatch = useDispatch();
  const query = useQuery({
    queryKey: queryKeys.allSkills,
    queryFn: async () => {
      const { data: { data } } = await httpRequest('get', '/settings/profile/skills/list/1/5000');
      return data[0];
    },
    enabled,
    staleTime: HOUR,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
  useEffect(() => {
    if (query.data) dispatch({ type: GET_ALL_SKILLS, payload: query.data });
  }, [query.data, dispatch]);
  return query;
}

export function useBootstrapReferenceData(enabled = true) {
  useCountries(enabled);
  useIndustries(enabled);
  useJobLevels(enabled);
  useAllSkills(enabled);
}

export function invalidateAllSkillsQuery(queryClient) {
  return queryClient.invalidateQueries({ queryKey: queryKeys.allSkills });
}

export function useJobs(page, limit, filters = {}, locationFilter = null, enabled = true) {
  const dispatch = useDispatch();
  const query = useQuery({
    queryKey: queryKeys.jobs(page, limit, { ...filters, locationFilter }),
    queryFn: async () => {
      const url = buildJobsUrl(page, limit, filters, locationFilter);
      const { data: { data } } = await httpRequest('get', url);
      return {
        jobs: data,
        total: Number(data?.total_item_count || 0),
      };
    },
    enabled,
    staleTime: 60 * 1000,
    keepPreviousData: true,
    refetchOnMount: true,
  });
  useEffect(() => {
    if (query.data?.jobs) {
      dispatch({ type: GET_ALL_JOBS, payload: query.data.jobs });
    }
  }, [query.data, dispatch]);
  return query;
}

export function useMyJobs(page = 1, limit = 1000) {
  const dispatch = useDispatch();
  const query = useQuery({
    queryKey: queryKeys.myJobs(page, limit),
    queryFn: async () => {
      const { data: { data } } = await httpRequest('get', `/jobs/byMe/${page}/${limit}`);
      // API returns an object with numeric keys; list lives at data["0"]
      if (Array.isArray(data)) {
        return data;
      }
      if (data && Array.isArray(data['0'])) {
        return data['0'];
      }
      return [];
    },
    staleTime: 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });
  useEffect(() => {
    if (query.data) {
      // Reducer expects payload["0"] as the jobs array
      dispatch({ type: GET_MY_JOBS, payload: { 0: query.data } });
    }
  }, [query.data, dispatch]);
  return query;
}

export function invalidateMyJobsQuery(queryClient, page = 1, limit = 1000) {
  return queryClient.invalidateQueries({ queryKey: queryKeys.myJobs(page, limit) });
}

export function useNotifications(options = {}) {
  const dispatch = useDispatch();
  const { enabled = true, refetchInterval = false } = options;
  const query = useQuery({
    queryKey: queryKeys.notifications,
    queryFn: async () => {
      const { data: { data } } = await httpRequest('get', '/settings/profile/notification/log/1/100');
      const notifications = data.notifications || [];
      const unreadCount = notifications.filter(
        (n) => n.is_read === 0 || n.is_read === '0'
      ).length;
      return { notifications, unreadCount };
    },
    enabled,
    staleTime: 20 * 1000,
    refetchInterval,
  });
  useEffect(() => {
    if (query.data) {
      dispatch({ type: GET_NOTIFICATIONS, payload: query.data.notifications });
      dispatch({ type: 'SET_UNREAD_NOTIFICATION_COUNT', payload: query.data.unreadCount });
    }
  }, [query.data, dispatch]);
  return query;
}

export function useWbLeague() {
  const dispatch = useDispatch();
  const query = useQuery({
    queryKey: queryKeys.wbLeague,
    queryFn: async () => {
      const { data: { data } } = await httpRequest('get', '/wbLeague');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
  useEffect(() => {
    if (query.data) {
      // Handle both old (array) and new ({league, user_opted_in}) response shapes
      const league = Array.isArray(query.data) ? query.data : (query.data.league || []);
      const userOptedIn = Array.isArray(query.data) ? false : (query.data.user_opted_in || false);
      dispatch({ type: GET_LEAGUE, payload: league, userOptedIn });
    }
  }, [query.data, dispatch]);
  return query;
}

export function useRecruiterOwn() {
  const dispatch = useDispatch();
  const query = useQuery({
    queryKey: queryKeys.recruiterOwn,
    queryFn: async () => {
      const { data: { data } } = await httpRequest('get', '/recruiter/own');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
  useEffect(() => {
    if (query.data) dispatch({ type: GET_MY_RECRUITER_DATA, payload: query.data });
  }, [query.data, dispatch]);
  return query;
}

export function useSidebarLeagueQueries() {
  useWbLeague();
  useRecruiterOwn();
}

export function useExperts() {
  const dispatch = useDispatch();
  const query = useQuery({
    queryKey: queryKeys.experts,
    queryFn: async () => {
      const { data: { data } } = await httpRequest('get', '/experts');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
  useEffect(() => {
    if (query.data) dispatch({ type: GET_ALL_EXPERT, payload: query.data });
  }, [query.data, dispatch]);
  return query;
}

export function useExpertCategories() {
  const dispatch = useDispatch();
  const query = useQuery({
    queryKey: queryKeys.expertCategories,
    queryFn: async () => {
      const { data: { data } } = await httpRequest('get', '/experts/category/all');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
  useEffect(() => {
    if (query.data) dispatch({ type: GET_ALL_EXPERT_CATEGORY, payload: query.data });
  }, [query.data, dispatch]);
  return query;
}

export function useProfile(enabled = true) {
  const dispatch = useDispatch();
  const query = useQuery({
    queryKey: queryKeys.profile,
    queryFn: async () => {
      const { data: { data } } = await httpRequest('get', '/settings/profile');
      const profileData = Array.isArray(data) ? data[0] : data;
      return profileData;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
  useEffect(() => {
    if (query.data) dispatch({ type: GET_PROFILE, payload: query.data });
  }, [query.data, dispatch]);
  return query;
}

export function useJobSeekerSubscription(enabled = true) {
  const dispatch = useDispatch();
  const query = useQuery({
    queryKey: queryKeys.jobSeekerSubscription,
    queryFn: async () => {
      const { data: { data } } = await httpRequest('get', '/job-seeker-subscriptions/my-subscription');
      return data?.subscription || null;
    },
    enabled,
    staleTime: 60 * 1000,
  });
  useEffect(() => {
    if (query.data !== undefined) {
      dispatch({ type: GET_MY_JOB_SEEKER_SUBSCRIPTION, payload: query.data });
    }
  }, [query.data, dispatch]);
  return query;
}

export function useActiveCampaign(enabled = true) {
  const query = useQuery({
    queryKey: queryKeys.activeCampaign,
    queryFn: async () => {
      const { data: { data } } = await httpRequest('get', '/job-seeker-subscriptions/campaign/active');
      const campaign = data?.campaign;
      if (campaign && !campaign.already_claimed) {
        return campaign;
      }
      return null;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
  return query;
}

export function useJobSeekerPlans(enabled = true) {
  const dispatch = useDispatch();
  const query = useQuery({
    queryKey: queryKeys.jobSeekerPlans,
    queryFn: async () => {
      const { data: { data } } = await httpRequest('get', '/job-seeker-subscriptions/plans');
      return data?.plans || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
  useEffect(() => {
    if (query.data) dispatch({ type: GET_JOB_SEEKER_PLANS, payload: query.data });
  }, [query.data, dispatch]);
  return query;
}

export function useProHunterTrial(enabled = true) {
  return useQuery({
    queryKey: queryKeys.proHunterTrial,
    queryFn: async () => {
      const { data: { data } } = await httpRequest('get', '/job-seeker-subscriptions/trial');
      return data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}
